import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { APIResponse, Auction, FreeKeyObject, TypeSnackbarPayload } from '../../utilities/types';
import {
  getBrowserNetworkInfo,
  getFormattedDate,
  getFormattedPrice,
  getFormattedQuantity,
  isAuctionFuture,
  isAuctionLive,
  parseIntoNumber,
  prepareAndGetMediaURL,
  userIsBuyer,
} from '../../utilities/helper';
import AuthFormModal from '../modals/auth-form.modal';
import FormModal from '../modals/form.modal';
import { RootState } from '../../store';
import { showSnackbar } from '../../store/slices/common.slice';
import { createBidApplication, getBidApplicationDetail, placeBidAPi } from '../../apis';
import CustomSwal from '../elements/custom-swal.element';
import BidWidget from './bid.widget';

interface AuctionWidgetProps {
  auctionDetail: Auction;
}

const AuctionWidget: React.FC<AuctionWidgetProps> = ({ auctionDetail }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const [showBidDetailModal, setShowBidDetailModal] = useState(false);
  const [showAuthFormModal, setShowAuthFormModal] = useState(false);
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [auction, setAuction] = useState<Auction>();

  const applyToPlaceBid = useCallback(
    async (comment?: string) => {
      try {
        const payload = {
          auction_id: auction?.id,
          buyer_id: user?.id,
          buyer_comment: comment,
        };
        const response = (await createBidApplication(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          CustomSwal.fire({
            icon: 'success',
            title: 'Bid Request Sent',
            text: `Request has been sent and waiting for approval`,
          });
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setIsPlacingBid(false);
    },
    [dispatch, user, auction]
  );

  const openBidRequestForm = useCallback(async () => {
    const result = await CustomSwal.fire({
      title: 'Apply for Bid',
      text: 'Please apply first to place bid and wait for approval',
      input: 'textarea',
      inputPlaceholder: 'Optional. Type your message here...',
      inputAttributes: {
        'aria-label': 'Type your message here',
        maxlength: '100',
      },
      allowOutsideClick: false,
      showCancelButton: true,
    });
    if (result.isConfirmed) applyToPlaceBid(result.value);
    else setIsPlacingBid(false);
  }, [applyToPlaceBid]);

  const openBidPlaceForm = useCallback(async () => {
    const nextBidMinAmount =
      parseIntoNumber(auction?.current_price) + parseIntoNumber(auction?.increment_price);
    const result = await CustomSwal.fire({
      title: 'Place Bid',
      text: 'Please enter the bid amount',
      html: (
        <Stack
          justifyContent="space-between"
          direction={{ xs: 'column', md: 'row' }}
          gap={2}
          sx={{
            backgroundColor: '#E8F5E9',
            borderRadius: 2,
            p: 2,
            my: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Current Bid:
            </Typography>
            <Typography variant="h5" color="success.main" fontWeight="bold">
              {getFormattedPrice(parseIntoNumber(auction?.current_price))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Total Quantity:
            </Typography>
            <Typography variant="h5" color="success.main" fontWeight="bold">
              {getFormattedQuantity(parseIntoNumber(auction?.quantity), auction?.quantity_unit)}
            </Typography>
          </Box>
        </Stack>
      ),
      input: 'number',
      inputValue: nextBidMinAmount,
      inputPlaceholder: 'Enter your bid price',
      inputAttributes: {
        min: nextBidMinAmount.toString(),
        step: '1',
      },
      allowOutsideClick: false,
      showCancelButton: true,
      preConfirm: (value) => {
        const price = parseIntoNumber(value);
        if (price <= 0) {
          CustomSwal.showValidationMessage('Please enter a valid price value.');
          return false;
        }
        if (nextBidMinAmount > price) {
          CustomSwal.showValidationMessage(
            `Your bid must be greater than ${getFormattedPrice(nextBidMinAmount)}`
          );
          return false;
        }
        return price;
      },
    });
    if (result.isConfirmed) {
      try {
        const payload = {
          auction_id: auction?.id,
          bidder_id: user?.id,
          network_info: await getBrowserNetworkInfo(),
          amount: result.value,
        };
        const response = (await placeBidAPi(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          if (response.data) {
            setAuction(response.data as Auction);
          }
          CustomSwal.fire({
            icon: 'success',
            title: 'Bid Place successfully',
          });
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
    }
    setIsPlacingBid(false);
  }, [dispatch, user, auction]);

  const checkForBidRequest = useCallback(async () => {
    try {
      setIsPlacingBid(true);
      const payload = {
        auction_id: auction?.id,
        buyer_id: user?.id,
      };
      const response = (await getBidApplicationDetail(payload)) as APIResponse<FreeKeyObject>;
      if (response.status) {
        if (!response.data) {
          openBidRequestForm();
        } else if (response.data.status === 'reject') {
          openBidRequestForm();
        } else if (response.data.status === 'pending') {
          setIsPlacingBid(false);
          CustomSwal.fire({
            icon: 'info',
            title: 'Bid Request Pending',
            text: `Your request to place bid on this auction is waiting for approval`,
          });
        } else {
          openBidPlaceForm();
        }
      }
    } catch (err: any) {
      setIsPlacingBid(false);
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [dispatch, user, openBidRequestForm, openBidPlaceForm, auction]);

  const startBidPlaceProcess = () => {
    if (isAuthenticated) {
      checkForBidRequest();
    } else {
      setShowAuthFormModal(true);
    }
  };

  const viewAuctionDetail = useCallback(() => {
    setShowBidDetailModal(true);
  }, []);

  useEffect(() => {
    setAuction(auctionDetail);
  }, [auctionDetail]);

  if (!auction) {
    return null;
  }

  const isComingAuction = isAuctionFuture(auction?.start_time as string);

  return (
    <>
      <Card
        sx={{
          maxWidth: 360,
          borderRadius: 4,
          boxShadow: 3,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 6,
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height="180"
            image={prepareAndGetMediaURL(auction.item?.image)}
            alt={auction.title}
          />
          <Chip
            label={auction.item?.name}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: '#ffffffee',
              fontWeight: 'bold',
            }}
          />
          {/* Top-right Bids */}
          {auction.custom_id && (
            <Chip
              label={`#${auction.custom_id}`}
              color="success"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
              }}
            />
          )}
          {isAuctionLive(auction.start_time || null, auction.end_time || null) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                backgroundColor: '#000000aa',
                color: '#fff',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.8rem',
              }}
            >
              <FiberManualRecordIcon sx={{ fontSize: 10, color: 'limegreen', mr: 0.5 }} />
              Live Auction
            </Box>
          )}
        </Box>

        {/* Content */}
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>{auction.title}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {auction.description}
          </Typography>

          <Box
            sx={{
              backgroundColor: '#E8F5E9',
              borderRadius: 2,
              p: 2,
              mt: 2,
            }}
          >
            {isComingAuction
              ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Quantity:
                  </Typography>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    {getFormattedQuantity(auction.quantity, auction.quantity_unit)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    <strong>Start At:</strong> {getFormattedDate(auction.start_time)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Company:</strong> {auction?.seller?.company_name ?? 'Not Provided'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> {auction?.seller?.address ?? 'Not Provided'}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Bid:
                  </Typography>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    {getFormattedPrice(auction.current_price)}
                  </Typography>

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    <strong>Starting Price:</strong> {getFormattedPrice(auction.starting_price)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Quantity:</strong>{' '}
                    {getFormattedQuantity(auction.quantity, auction.quantity_unit)}
                  </Typography>
                  {isAuctionLive(auction.start_time || null, auction.end_time || null) ? (
                    <Typography variant="body2">
                      <strong>Ended At:</strong> {getFormattedDate(auction.end_time)}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>Start At:</strong> {getFormattedDate(auction.start_time)}
                    </Typography>
                  )}
                </>
              )}
          </Box>
          {!userIsBuyer(user) ? (
            <Button
              variant="contained"
              color="info"
              fullWidth
              sx={{ mt: 2, fontWeight: 'bold' }}
              onClick={viewAuctionDetail}
            >
              View Details
            </Button>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'start', sm: 'space-between' }}
              gap={2}
            >
              {isAuctionLive(auction.start_time || null, auction.end_time || null) && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  sx={{ mt: 2, fontWeight: 'bold' }}
                  onClick={startBidPlaceProcess}
                  disabled={isPlacingBid}
                >
                  {isPlacingBid ? 'Placing Bid' : 'Place Bid'}
                </Button>
              )}
              <Button
                variant="contained"
                color="info"
                fullWidth
                sx={{ mt: 2, fontWeight: 'bold' }}
                onClick={viewAuctionDetail}
              >
                View Details
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
      {showBidDetailModal && (
        <FormModal
          open={showBidDetailModal}
          onClose={() => setShowBidDetailModal(false)}
          size="lg"
          title="Auction Detail"
        >
          <BidWidget auction_id={parseIntoNumber(auction.id)} allowPlaceBid={userIsBuyer(user)} />
        </FormModal>
      )}
      {showAuthFormModal && (
        <AuthFormModal
          open={showAuthFormModal}
          onClose={() => setShowAuthFormModal(false)}
          onDone={() => setShowAuthFormModal(false)}
          size="xs"
        />
      )}
    </>
  );
};

export default AuctionWidget;
